import { forwardRef, useMemo } from 'react';
import { chain, sumBy } from 'lodash';
import { DataTableExpandedRows } from 'primereact/datatable';
import PrintableWrapper from '../../components/PrintableWrapper';
import { situacaoParse } from '../../utils/constants';
import useLocalStore from '../../hooks/use-local-store';
import GeneralInfoTable from './create-edit/resumo/components/general-info-table';
import ResumoInsumosTable from './create-edit/resumo/components/insumos-table';
import {
  calculaQuantidadeInsumo,
  isSegundoTratamento,
} from './create-edit/utils';
import { DataToPrintTS, InsumoRow } from './types';

type PrintableProps = {
  data: DataToPrintTS;
  insumos?: InsumoRow[];
};

const Printable = forwardRef<HTMLDivElement, PrintableProps>(
  ({ data, insumos }, ref) => {
    const user = useLocalStore('user');
    const expandedRows: DataTableExpandedRows | undefined = useMemo(() => {
      if (!data.tratamentosParsed?.length) {
        return;
      }

      return data.tratamentosParsed.reduce<DataTableExpandedRows>(
        (acc, tratamento) => {
          if (tratamento.identification) {
            acc[tratamento.identification] = true;
          }
          return acc;
        },
        {},
      );
    }, [data.tratamentosParsed]);

    const insumosParsed: InsumoRow[] = useMemo(() => {
      if (insumos?.length) return insumos;

      return chain(data.tratamentosParsed)
        .map((tratamento) =>
          tratamento.defensivos.map((item) => ({
            ...item,
            tratamentoPeso: tratamento.peso,
            tratamentoDose: item.dose,
            tratamentoQuantidade: tratamento.quantidade,
            segundoTratamento: isSegundoTratamento(tratamento),
          })),
        )
        .flatten()
        .groupBy('item.id')
        .map<InsumoRow>((defensivos) => {
          const defensivosComTotal = defensivos.map((item) => {
            const total = item.segundoTratamento
              ? item.quantidade || 0
              : calculaQuantidadeInsumo({
                  dose: item.tratamentoDose || 0,
                  peso: item.tratamentoPeso || 0,
                  quantidade: item.tratamentoQuantidade || 0,
                });

            return {
              ...item,
              total,
            };
          });

          return {
            id: defensivos[0].item?.id,
            descricao: defensivos[0].item?.descricao,
            total: sumBy(defensivosComTotal, 'total'),
            unidade: defensivos[0].item?.unidade,
          };
        })
        .value();
    }, [data, insumos]);

    const recomendante = user.value?.nome_completo;

    const situacao =
      data.status && situacaoParse[data.status]
        ? situacaoParse[data.status]?.label
        : '';

    return (
      <div className="hidden-container">
        <PrintableWrapper ref={ref}>
          <div className="titulo-print-container">
            <h2 className="pl-2">{`Recomendação de Semeadura ${
              data.id || ''
            }`}</h2>
            <h4 className="pr-4">{data.dataHoraImpressao}</h4>
          </div>
          <span className="pl-2 text-large">
            <strong>Status: </strong>
            {situacao}
          </span>
          <div>
            <h2 className="pl-2">Tratamento de sementes</h2>
            <GeneralInfoTable
              tratamentos={data.tratamentosParsed || []}
              expandedRows={expandedRows}
            />
            <ResumoInsumosTable insumos={insumosParsed} />
          </div>
          <div className="pt-5 px-5">
            <span>
              <strong>Propriedade: </strong>
            </span>
            {data.propriedade && <span>{data.propriedade.descricao}</span>}
            <br />
            <span>
              <strong>Safra: </strong>
            </span>
            {data.safra && <span>{data.safra.descricao}</span>}
            <br />
            <span>
              <strong>Atividade: </strong>
            </span>
            {data.atividade && <span>{data.atividade.descricao}</span>}

            <div className="print-footer-rubric-container">
              <div className="print-footer-rubric">
                <p>_____________________________</p>
                <span>{recomendante}</span>
                <br />
                <span>
                  <strong>Recomendante</strong>
                </span>
              </div>
              <div className="print-footer-rubric">
                <p>_____________________________</p>
                <p>
                  <strong>Responsável</strong>
                </p>
              </div>
            </div>
          </div>
        </PrintableWrapper>
      </div>
    );
  },
);

Printable.displayName = 'Printable';

export default Printable;
